import { useEffect, useState } from "react";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { useDataLayerValue } from "../../DataLayer/DataLayer";
import {
  createEntertainmentMenuItem,
  updateEntertainmentMenuItem,
} from "../../utils/ApiCalls/BackOffice/Entertainment";
import { addlog } from "../../utils/ApiCalls/Logs";
import { ENTERTAINMENT_LOG_CATEGORIES, LOG_ACTIONS, LOG_TYPES } from "../../utils/Constant/LogConstant";

const FIELD_LABELS = {
  itemizerId: "Itemizer",
  name: "Name",
  nameSimplifiedChinese: "Name (SC)",
  nameTraditionalChinese: "Name (TC)",
  description: "Description",
  price: "Price",
};

// Builds "Price: 10 -> 12, Name: Old -> New" from only the fields that actually changed
const buildFieldChanges = (oldValues, newValues, resolveValue = (v) => v) =>
  Object.keys(FIELD_LABELS)
    .filter((key) => String(oldValues[key]) !== String(newValues[key]))
    .map(
      (key) =>
        `${FIELD_LABELS[key]}: ${resolveValue(key, oldValues[key])} -> ${resolveValue(key, newValues[key])}`
    )
    .join(", ");

const EntertainmentMenuItemInput = ({ rvcId, rvcName, itemizers, menuItem, onSuccess, readOnly }) => {
  const [itemizerId, setItemizerId] = useState("");
  const [itemNumber, setItemNumber] = useState("");
  const [name, setName] = useState("");
  const [nameSimplifiedChinese, setNameSimplifiedChinese] = useState("");
  const [nameTraditionalChinese, setNameTraditionalChinese] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [{ userDetails }, dispatch] = useDataLayerValue();
  const [error, setError] = useState();

  const isEdit = !!menuItem?.id;

  useEffect(() => {
    if (menuItem?.id) {
      setItemizerId(menuItem.itemizerId ?? "");
      setItemNumber(menuItem.itemNumber || "");
      setName(menuItem.name || "");
      setNameSimplifiedChinese(menuItem.nameSimplifiedChinese || "");
      setNameTraditionalChinese(menuItem.nameTraditionalChinese || "");
      setDescription(menuItem.description || "");
      setPrice(menuItem.price ?? "");
    }
  }, [menuItem]);

  // default to the first itemizer on create, once the list has loaded
  useEffect(() => {
    if (!menuItem?.id && itemizerId === "" && itemizers?.length) {
      setItemizerId(itemizers[0].id);
    }
  }, [itemizers, menuItem, itemizerId]);

  const submitHandler = async (e) => {
    e.preventDefault();

    if (itemizerId === "" || !name || price === "" || (!isEdit && !itemNumber)) {
      const message = "Error: The following fields are empty - itemizer, item number, name, price";
      dispatch({
        type: "SET_ERROR_MESSAGE",
        errMess: { message, type: "error" },
      });
      setError(message);
      return;
    }

    if (!isEdit && !/^\d{1,10}$/.test(itemNumber)) {
      const message = "Error: Item number must be digits only, up to 10 characters";
      dispatch({
        type: "SET_ERROR_MESSAGE",
        errMess: { message, type: "error" },
      });
      setError(message);
      return;
    }

    setError();

    const values = {
      itemizerId,
      name,
      nameSimplifiedChinese,
      nameTraditionalChinese,
      description,
      price: Number(price),
      ...(isEdit ? {} : { rvcId, itemNumber }),
    };

    const data = isEdit
      ? await updateEntertainmentMenuItem(menuItem.id, values)
      : await createEntertainmentMenuItem(values);

    if (data?.error) {
      dispatch({
        type: "SET_ERROR_MESSAGE",
        errMess: { message: data.error, type: "error" },
      });
      return;
    }

    dispatch({
      type: "SET_ERROR_MESSAGE",
      errMess: {
        message: isEdit ? "Menu item updated" : "Menu item created",
        type: "success",
      },
    });

    let action = LOG_ACTIONS.CREATED;
    if (isEdit) {
      const resolveValue = (key, val) =>
        key === "itemizerId" ? itemizers?.find((it) => it.id === val)?.itemizerCode || val : val;
      const changes = buildFieldChanges(
        {
          itemizerId: menuItem.itemizerId,
          name: menuItem.name,
          nameSimplifiedChinese: menuItem.nameSimplifiedChinese,
          nameTraditionalChinese: menuItem.nameTraditionalChinese,
          description: menuItem.description,
          price: menuItem.price,
        },
        values,
        resolveValue
      );
      action = changes ? `${LOG_ACTIONS.UPDATED} (${changes})` : LOG_ACTIONS.UPDATED;
    }

    addlog(
      action,
      LOG_TYPES.ENTERTAINMENT_RVC,
      {
        id: menuItem?.id || data?.data?.id,
        rvcId,
        title: `${rvcName ? rvcName + " - " : ""}${name} (#${menuItem?.itemNumber || itemNumber})`,
        category: ENTERTAINMENT_LOG_CATEGORIES.MENU_ITEM,
      },
      userDetails.id
    );

    onSuccess?.(data?.data || data);
  };

  return (
    <div>
      <section className="content">
        {error && (
          <div className="d-flex justify-content-center align-items-center text-danger">
            <p>{error}</p>
          </div>
        )}

        <div className="container-fluid">
          <div className="card" style={readOnly ? { opacity: 0.6, pointerEvents: "none" } : undefined}>
            <form className="input_form" onSubmit={submitHandler}>
              <div className="card-body">
                <div className="row">
                  <div className="col-lg-4 col-md-12 col-sm-12 col-12">
                    <div className="fieldrow">
                      <label htmlFor="itemNumber" className="f_label">
                        Item Number {!isEdit && <span style={{ color: "red" }}>*</span>}
                      </label>
                      <input
                        id="itemNumber"
                        required={!isEdit}
                        disabled={readOnly || isEdit}
                        className="f_input"
                        type="text"
                        inputMode="numeric"
                        maxLength={10}
                        placeholder="e.g. 701004268"
                        value={itemNumber}
                        onChange={(e) => setItemNumber(e.target.value.replace(/\D/g, "").slice(0, 10))}
                      />
                    </div>
                  </div>

                  <div className="col-lg-4 col-md-12 col-sm-12 col-12">
                    <div className="fieldrow">
                      <label htmlFor="name" className="f_label">Menu Item Name <span style={{ color: "red" }}>*</span></label>
                      <input
                        id="name"
                        required
                        disabled={readOnly}
                        className="f_input"
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="col-lg-4 col-md-12 col-sm-12 col-12">
                    <div className="fieldrow">
                      <label htmlFor="nameSimplifiedChinese" className="f_label">Name (Simplified Chinese)</label>
                      <input
                        id="nameSimplifiedChinese"
                        disabled={readOnly}
                        className="f_input"
                        type="text"
                        value={nameSimplifiedChinese}
                        onChange={(e) => setNameSimplifiedChinese(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="col-lg-4 col-md-12 col-sm-12 col-12">
                    <div className="fieldrow">
                      <label htmlFor="nameTraditionalChinese" className="f_label">Name (Traditional Chinese)</label>
                      <input
                        id="nameTraditionalChinese"
                        disabled={readOnly}
                        className="f_input"
                        type="text"
                        value={nameTraditionalChinese}
                        onChange={(e) => setNameTraditionalChinese(e.target.value)}
                      />
                    </div>
                  </div>



                  <div className="col-lg-4 col-md-12 col-sm-12 col-12">
                    <div className="fieldrow">
                      <label htmlFor="itemizerId" className="f_label">Itemizer <span style={{ color: "red" }}>*</span></label>
                      <Select
                        id="itemizerId"
                        size="small"
                        fullWidth
                        displayEmpty
                        disabled={readOnly}
                        value={itemizerId}
                        onChange={(e) => setItemizerId(e.target.value)}
                      >
                        <MenuItem value="" disabled>
                          Select itemizer
                        </MenuItem>
                        {(itemizers || []).map((it) => (
                          <MenuItem key={it.id} value={it.id}>
                            {it.itemizerCode} ({it.itemizerId})
                          </MenuItem>
                        ))}
                      </Select>
                    </div>
                  </div>

                  <div className="col-lg-4 col-md-12 col-sm-12 col-12">
                    <div className="fieldrow">
                      <label htmlFor="price" className="f_label">Price <span style={{ color: "red" }}>*</span></label>
                      <input
                        id="price"
                        required
                        disabled={readOnly}
                        className="f_input"
                        type="number"
                        step="0.01"
                        min={0}
                        value={price}
                        onChange={(e) => setPrice(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="col-lg-4 col-md-12 col-sm-12 col-12">
                    <div className="fieldrow">
                      <label htmlFor="description" className="f_label">Menu Item Description</label>
                      <input
                        id="description"
                        disabled={readOnly}
                        className="f_input"
                        type="text"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                      />
                    </div>
                  </div>

                </div>
              </div>

              {!readOnly && (
                <div className="card-footer text-right">
                  <button className="btn main_submit">
                    {isEdit ? "Update" : "Add"} menu item
                  </button>
                </div>
              )}
            </form>
          </div>
          <div className="pt-3"></div>
        </div>
      </section>
    </div>
  );
};

export default EntertainmentMenuItemInput;
